import type { FetchQuoteParams } from 'actions/InsuranceActions'
import { fetchInsuranceQuote } from 'actions/InsuranceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isNonEmptyArray } from 'lib/array/arrayUtils'
import { getCheapestEconomyFareFamilyWithBaggage } from 'lib/flights/flightUtils'
import { getInsuranceQuoteParams } from 'lib/insurance/insuranceFlights'
import { INSURANCE_PRODUCT_ID } from 'constants/insurance'
import { getInsuranceQuoteKey } from 'lib/insurance/insuranceUtils'
import { useEffect, useMemo } from 'react'

function useFlightsSearchInsuranceQuote(
  fareFamilies?: {
    families: Array<App.FlightFareFamily>
    familiesPerLeg: Array<Array<App.FlightFareFamily>>
  },
  occupants?: App.Occupants,
  destinationCountries?: Array<string>,
  departingDate?: string,
  arrivalDate?: string,
  options?: { disabled?: boolean },
  isReturnType?: boolean,
  isCombinedUpsell?: boolean,
  departingFareFamily?: App.FlightFareFamily,
) {
  const dispatch = useAppDispatch()

  const params = useMemo(() => {
    if (!fareFamilies?.families.length && !fareFamilies?.familiesPerLeg.length || options?.disabled) return

    // In case flights are handled separately we have only the current farefamily leg
    if (!isCombinedUpsell) {
      const { cheapestEconomyWithBaggage, pairedReturningFareFamily, journeyTotalCost } = getCheapestEconomyFareFamilyWithBaggage(fareFamilies.families, isReturnType)

      if (!cheapestEconomyWithBaggage) return
      if (isReturnType && !pairedReturningFareFamily) return

      return [getInsuranceQuoteParams(departingDate, arrivalDate, journeyTotalCost + (!isReturnType && departingFareFamily?.withEmbeddedInsurance ? departingFareFamily.cost.all : 0), destinationCountries, occupants)]
    // Otherwise, flights are handled combined and we have all flags in familiesPerLeg
    } else if (isNonEmptyArray(fareFamilies.familiesPerLeg) && isNonEmptyArray(fareFamilies.familiesPerLeg[0])) {
      const result: Array<FetchQuoteParams> = []
      const processedCosts = new Set<number>()

      const { cheapestEconomyWithBaggage: departingEligible } = getCheapestEconomyFareFamilyWithBaggage(fareFamilies.familiesPerLeg[0], false)

      // iterate over departing skipping the eligible departing, if it exists
      for (const family of fareFamilies.familiesPerLeg[0]) {
        const isTheEligibleDeparting = departingEligible && family.id === departingEligible.id
        // If the departing is the eligible one for insurance, we request quote for it only, and also for the permutation of eligible departing + eligible returning linked to it, if any eligible returning exists
        // Otherwise, we request quote for the eligible returning linked to the departing

        if (isTheEligibleDeparting) {
          if (!processedCosts.has(departingEligible.cost.all)) {
            result.push(getInsuranceQuoteParams(departingDate, arrivalDate, departingEligible.cost.all, destinationCountries, occupants))
            processedCosts.add(departingEligible.cost.all)
          }
        }

        if (isNonEmptyArray(fareFamilies.familiesPerLeg[1])) {
          const linkedReturnFareFamilies = fareFamilies.familiesPerLeg[1].filter(returnFamily => returnFamily.pairedUpsellIds?.includes(family.id))
          const { cheapestEconomyWithBaggage: returningEligible } = getCheapestEconomyFareFamilyWithBaggage(linkedReturnFareFamilies, false)

          if (returningEligible) {
            if (isTheEligibleDeparting) {
              if (!processedCosts.has(departingEligible.cost.all + returningEligible.cost.all)) {
                result.push(getInsuranceQuoteParams(departingDate, arrivalDate, departingEligible.cost.all + returningEligible.cost.all, destinationCountries, occupants))
                processedCosts.add(departingEligible.cost.all + returningEligible.cost.all)
              }
            }

            if (!processedCosts.has(returningEligible.cost.all)) {
              result.push(getInsuranceQuoteParams(departingDate, arrivalDate, returningEligible.cost.all, destinationCountries, occupants))
              processedCosts.add(returningEligible.cost.all)
            }
          }
        }
      }

      return result
    }
  }, [arrivalDate, departingDate, destinationCountries, fareFamilies?.families, fareFamilies?.familiesPerLeg, occupants, options?.disabled, isReturnType, isCombinedUpsell, departingFareFamily?.withEmbeddedInsurance, departingFareFamily?.cost.all])

  useEffect(() => {
    if (!params?.length || options?.disabled) return

    for (const currentParams of params) {
      if (
        currentParams.startDate &&
        currentParams.endDate &&
        currentParams.coverAmount &&
        currentParams.destinationCountries?.length &&
        currentParams.travellers
      ) {
        dispatch(fetchInsuranceQuote(INSURANCE_PRODUCT_ID.CFMR, currentParams))
      }
    }
  }, [dispatch, options?.disabled, params])

  const quotes = useAppSelector(state => state.insurance.quotes)
  const filteredQuotes = useMemo(() => {
    const keys = params?.map(currentParams => getInsuranceQuoteKey(INSURANCE_PRODUCT_ID.CFMR, currentParams))
    return keys?.reduce((acc, key) => {
      if (quotes[key]) {
        acc[key] = quotes[key]
      }
      return acc
    }, {} as Record<string, App.InsuranceQuote>) || {}
  }, [params, quotes])

  const fetchingQuotes = useAppSelector(state => state.insurance.fetchingQuotes)
  const filteredFetchingQuotes = useMemo(() => {
    const keys = params?.map(currentParams => getInsuranceQuoteKey(INSURANCE_PRODUCT_ID.CFMR, currentParams))
    return keys?.map(key => fetchingQuotes[key]).filter(Boolean)
  }, [fetchingQuotes, params])

  const fetchQuoteErrors = useAppSelector(state => state.insurance.fetchQuotesErrors)
  const errors = useMemo(() => {
    const keys = params?.map(currentParams => getInsuranceQuoteKey(INSURANCE_PRODUCT_ID.CFMR, currentParams))
    return keys?.map(key => fetchQuoteErrors[key]).filter(Boolean)
  }, [fetchQuoteErrors, params])

  return {
    quotes: filteredQuotes || {},
    fetching: !!filteredFetchingQuotes?.length,
    error: !!errors?.length,
  }
}

export default useFlightsSearchInsuranceQuote
